import React, {useEffect, useRef, useState} from 'react'
import {Box} from "@mui/material"
import SignalCellularAltRoundedIcon from "@mui/icons-material/SignalCellularAltRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import {mount, reinitialize, unmount} from "booking-engine-widget/src";
import {BookingEngineClientConfig, BookingEngineUIConfig} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";
import { useOrganization } from 'crqlar-react-common/dist/state/auth.reducer';
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    widgetId?: string,
    widgetName: string,
    className?: string
    widgetConfig?: BookingEngineClientConfig
    uiConfig?: BookingEngineUIConfig
}

declare global {
    interface Window {
        bookingWidget: {
            mount: typeof mount;
            unmount: typeof unmount;
            reinitialize: typeof reinitialize;
        };
    }
}

const setCssVar = (el: HTMLElement, name: string, value: string | undefined) => {
    if (value) el.style.setProperty(name, value)
    else el.style.removeProperty(name)
}

const setPxCssVar = (el: HTMLElement, name: string, value: number | undefined) => {
    if (value !== undefined) el.style.setProperty(name, `${value}px`)
    else el.style.removeProperty(name)
}

const Preview = ({widgetId, widgetName, className, widgetConfig, uiConfig}: Props) => {

    const organization = useOrganization()
    const holderRef = useRef<HTMLDivElement>(null);
    const [currentTime, setCurrentTime] = useState(() => new Date())
    const [widgetLoadVersion, setWidgetLoadVersion] = useState(0)
    const timeLocale = uiConfig?.defaultLanguageCode === 'de' ? 'de-DE' : 'en-US'
    const formattedTime = new Intl.DateTimeFormat(timeLocale, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(currentTime)

    useEffect(() => {
        const intervalId = window.setInterval(() => setCurrentTime(new Date()), 60_000)
        return () => window.clearInterval(intervalId)
    }, [])

    // The live preview mounts the real guest-facing booking widget (real public key), so a
    // read-only staff user could otherwise drive it and create a real reservation. Marking the
    // shadow host `inert` propagates through the flattened tree into the widget's shadow DOM and
    // makes every control — the service/profile autocompletes and all buttons — non-interactive,
    // while the preview stays fully visible so styling/config can still be reviewed.
    const readOnly = useReadOnly()
    useEffect(() => {
        if (holderRef.current) {
            holderRef.current.inert = readOnly
        }
    }, [readOnly, widgetName, widgetConfig]);

    // mount() creates the shadow root asynchronously after localization has initialized.
    useEffect(() => {
        const handleWidgetLoaded = () => setWidgetLoadVersion(version => version + 1)
        window.addEventListener('bookingWidgetLoaded', handleWidgetLoaded)

        return () => window.removeEventListener('bookingWidgetLoaded', handleWidgetLoaded)
    }, [])

    useEffect(() => {
        window.bookingWidget?.mount();

        return () => {
            window.bookingWidget?.unmount();
        }
    }, [widgetName, widgetConfig]);

    useEffect(() => {
        if (!holderRef.current) return

        const {
            backgroundColor,
            borderRadius,
            fieldBorderRadius,
            buttonBorderRadius,
            textColor,
            fieldBorderColor,
            actionButtonBackgroundColor,
            actionButtonTextColor,
            actionButtonBackgroundDisabled,
            fontFamily,
            fontUrl
        } = uiConfig ?? {}

        const el = holderRef.current
        setCssVar(el, '--crqlar-background-color', backgroundColor)
        setCssVar(el, '--crqlar-action-button-bg-color', actionButtonBackgroundColor)
        setCssVar(el, '--crqlar-action-button-text-color', actionButtonTextColor)
        setCssVar(el, '--crqlar-action-button-bg-disabled', actionButtonBackgroundDisabled)
        setPxCssVar(el, '--crqlar-border-radius', borderRadius)
        setPxCssVar(el, '--crqlar-field-border-radius', fieldBorderRadius)
        setPxCssVar(el, '--crqlar-button-border-radius', buttonBorderRadius)
        setCssVar(el, '--crqlar-text-color', textColor)
        setCssVar(el, '--field-border-color', fieldBorderColor)
        setCssVar(el, '--crqlar-font-family', fontFamily)

        // Also inject !important rules directly into the shadow root to beat Emotion/MUI specificity
        const shadowRoot = holderRef.current.shadowRoot
        if (!shadowRoot) return

        // Inject font @import into shadow root so the font is available inside it
        const fontImportId = 'crqlar-font-import'
        let fontImportEl = shadowRoot.querySelector<HTMLStyleElement>(`#${fontImportId}`)
        if (!fontImportEl) {
            fontImportEl = document.createElement('style')
            fontImportEl.id = fontImportId
            shadowRoot.insertBefore(fontImportEl, shadowRoot.firstChild)
        }
        fontImportEl.textContent = fontUrl
            ? `@import url('${fontUrl}');`
            : ''

        const styleId = 'crqlar-live-preview-styles'
        let styleEl = shadowRoot.querySelector<HTMLStyleElement>(`#${styleId}`)
        if (!styleEl) {
            styleEl = document.createElement('style')
            styleEl.id = styleId
            shadowRoot.appendChild(styleEl)
        }

        const rules: string[] = []
        rules.push(`
            .booking-widget-container {
                height: 100% !important;
                min-height: 0 !important;
                overflow: hidden !important;
            }

            .crqlar-booking-widget-content {
                height: 100% !important;
                min-height: 0 !important;
                gap: 8px !important;
                padding: 8px 16px 32px !important;
                overflow-x: hidden !important;
                overflow-y: auto !important;
                overscroll-behavior: contain;
            }
        `)
        if (backgroundColor) {
            rules.push(`.crqlar-booking-widget-content { background-color: ${backgroundColor} !important; }`)
            rules.push(`
                .crqlar-booking-widget-content .mbsc-calendar,
                .crqlar-booking-widget-content .mbsc-calendar-wrapper,
                .crqlar-booking-widget-content .mbsc-datepicker,
                .crqlar-booking-widget-content .mbsc-popup-body,
                .crqlar-booking-widget-content .mbsc-popup,
                .crqlar-booking-widget-content .mbsc-calendar-header,
                .crqlar-booking-widget-content .mbsc-calendar-grid-wrapper,
                .crqlar-booking-widget-content .MuiPaper-root,
                .crqlar-booking-widget-content .MuiMenu-paper,
                .crqlar-booking-widget-content .MuiAutocomplete-paper,
                .crqlar-booking-widget-content .MuiMenuItem-root
                { background-color: ${backgroundColor} !important; }
            `)

            // Browser autocompletion color handling
            rules.push(`
                .crqlar-booking-widget-content input:-webkit-autofill,
                .crqlar-booking-widget-content input:-webkit-autofill:hover,
                .crqlar-booking-widget-content input:-webkit-autofill:focus,
                .crqlar-booking-widget-content input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px ${backgroundColor} inset !important;
                    box-shadow: 0 0 0 30px ${backgroundColor} inset !important;
                    ${textColor ? `-webkit-text-fill-color: ${textColor} !important;` : ''}
                }
            `)
        }
        rules.push(`.crqlar-booking-widget-content { border-radius: ${borderRadius ?? 12}px !important; }`)
        rules.push(`.crqlar-booking-widget-content .MuiOutlinedInput-root, .crqlar-booking-widget-content .MuiAutocomplete-inputRoot { border-radius: ${fieldBorderRadius ?? 6}px !important; }`)
        rules.push(`.crqlar-booking-widget-content .MuiButton-root { border-radius: ${buttonBorderRadius ?? fieldBorderRadius ?? 12}px !important; }`)
        if (textColor) {
            rules.push(`
                .crqlar-booking-widget-content .MuiTypography-root,
                .crqlar-booking-widget-content .MuiInputBase-input,
                .crqlar-booking-widget-content .MuiInputLabel-root,
                .crqlar-booking-widget-content .MuiButton-root,
                .crqlar-booking-widget-content .MuiSelect-select,
                .crqlar-booking-widget-content .mbsc-calendar-day-text,
                .crqlar-booking-widget-content .mbsc-calendar-title,
                .crqlar-booking-widget-content .mbsc-calendar-month-title,
                .crqlar-booking-widget-content .mbsc-calendar-year-title,
                .crqlar-booking-widget-content .mbsc-calendar-week-day,
                .crqlar-booking-widget-content .mbsc-calendar-label-text,
                .crqlar-booking-widget-content .mbsc-calendar-button,
                .widget-title, .footer-text, .service-option-title,
                .service-option-description, .service-option-price,
                .time-option-value, .time-option-price,
                .tos-message, .newsletter-policy, .basket-date-value,
                .basket-item-service-name, .basket-time-value, .basket-item-rate-name,
                .message, .status-message-main, .status-message-sub,
                .basket-date-icon, .basket-time-icon, .basket-item-rate-price
                { color: ${textColor} !important; }
            `)
        }

        if(fieldBorderColor) {
            rules.push(
                `.crqlar-booking-widget-content .MuiSvgIcon-root
                { color: ${fieldBorderColor} !important; }
                `
            )
        }

        if (textColor) rules.push(`:host { --crqlar-text-color: ${textColor}; }`)

        if (actionButtonBackgroundColor) {
            rules.push(`:host { --crqlar-action-button-bg-color: ${actionButtonBackgroundColor}; }`)
            rules.push(`.crqlar-booking-widget-content .MuiButton-contained { background-color: ${actionButtonBackgroundColor} !important; }`)
        }
        if (actionButtonTextColor) {
            rules.push(`:host { --crqlar-action-button-text-color: ${actionButtonTextColor}; }`)
            rules.push(`.crqlar-booking-widget-content .MuiButton-contained { color: ${actionButtonTextColor} !important; }`)
        }
        if (actionButtonBackgroundDisabled) {
            rules.push(`:host { --crqlar-action-button-bg-disabled: ${actionButtonBackgroundDisabled}; }`)
            rules.push(`.crqlar-booking-widget-content .MuiButton-contained.Mui-disabled { background-color: ${actionButtonBackgroundDisabled} !important; }`)
        }
        // Border color: use explicit fieldBorderColor if set, otherwise MUI grey defaults
        const borderNormal = fieldBorderColor ?? 'rgba(0, 0, 0, 0.23)'
        const borderActive = fieldBorderColor ?? 'rgba(0, 0, 0, 0.87)'
        rules.push(`.crqlar-booking-widget-content .MuiOutlinedInput-notchedOutline { border-color: ${borderNormal} !important; }`)
        rules.push(`.crqlar-booking-widget-content .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline { border-color: ${borderActive} !important; }`)
        rules.push(`.crqlar-booking-widget-content .MuiOutlinedInput-root.Mui-focused:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline { border-color: ${borderActive} !important; }`)
        if (fontFamily) rules.push(`
            .crqlar-booking-widget-content,
            .crqlar-booking-widget-content .MuiTypography-root,
            .crqlar-booking-widget-content .MuiInputBase-input,
            .crqlar-booking-widget-content .MuiInputLabel-root,
            .crqlar-booking-widget-content .MuiButton-root,
            .crqlar-booking-widget-content .MuiMenuItem-root
            { font-family: ${fontFamily} !important; }
        `)

        styleEl.textContent = rules.join('\n')

    }, [widgetLoadVersion, widgetConfig, uiConfig?.backgroundColor, uiConfig?.borderRadius, uiConfig?.fieldBorderRadius, uiConfig?.buttonBorderRadius, uiConfig?.textColor, uiConfig?.fieldBorderColor, uiConfig?.actionButtonBackgroundColor, uiConfig?.actionButtonTextColor, uiConfig?.actionButtonBackgroundDisabled, uiConfig?.fontFamily, uiConfig?.fontUrl]);

    //endregion Handlers

    //region UI

    return (
        <Box
            sx={{
                position: "sticky",
                top: "40px",
                display: "flex",
                width: "auto",
                height: "min(720px, calc(100dvh - 88px))",
                // Favor widget readability over matching an exact hardware aspect ratio.
                aspectRatio: "9 / 17",
                maxWidth: "100%",
                minWidth: 0,
                flexShrink: 0,
                alignSelf: "flex-start",
                boxSizing: "border-box",
                overflow: "visible",
                border: "8px solid",
                borderColor: "grey.900",
                borderRadius: "45px",
                backgroundColor: "grey.900",
                boxShadow: "0 16px 32px rgba(15, 23, 42, 0.16)",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: "14%",
                    left: "-14px",
                    width: 6,
                    height: 32,
                    borderRadius: "4px 0 0 4px",
                    backgroundColor: "grey.900",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.24)",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: "24%",
                    left: "-14px",
                    width: 6,
                    height: 48,
                    borderRadius: "4px 0 0 4px",
                    backgroundColor: "grey.900",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.24)",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: "34%",
                    left: "-14px",
                    width: 6,
                    height: 48,
                    borderRadius: "4px 0 0 4px",
                    backgroundColor: "grey.900",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.24)",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: "24%",
                    right: "-14px",
                    width: 6,
                    height: 64,
                    borderRadius: "0 4px 4px 0",
                    backgroundColor: "grey.900",
                    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.24)",
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    minHeight: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    borderRadius: "37px",
                    backgroundColor: uiConfig?.backgroundColor || "common.white",
                    boxShadow: "inset 0 0 0 3px rgba(63, 63, 70, 0.4)",
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                        height: 44,
                        px: 2.25,
                        boxSizing: "border-box",
                        color: uiConfig?.textColor || "grey.900",
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            minWidth: 64,
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1,
                            textAlign: "center",
                        }}
                    >
                        {formattedTime}
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: "50%",
                            width: 90,
                            height: 22,
                            borderRadius: "11px",
                            backgroundColor: "grey.900",
                            boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
                            transform: "translateX(-50%)",
                        }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.25,
                            minWidth: 64,
                        }}
                    >
                        <SignalCellularAltRoundedIcon sx={{fontSize: 17}}/>
                        <WifiRoundedIcon sx={{fontSize: 18}}/>
                        <Box
                            sx={{
                                position: "relative",
                                width: 20,
                                height: 10,
                                ml: 0.25,
                                boxSizing: "border-box",
                                border: "1.5px solid currentColor",
                                borderRadius: "2px",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    inset: 1,
                                    borderRadius: "0.5px",
                                    backgroundColor: "currentColor",
                                },
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    top: 2,
                                    right: -3,
                                    width: 2,
                                    height: 5,
                                    borderRadius: "0 1px 1px 0",
                                    backgroundColor: "currentColor",
                                    opacity: 0.8,
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "center",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    {
                        //@ts-ignore
                        <div className={className} widget_id={widgetId} public_key={organization?.publicKey}
                             ref={holderRef}
                             // `inert` (set above) blocks interaction; pointer-events is a CSS fallback
                             // that also cascades into the shadow tree for read-only users.
                             style={{
                                 display: 'flex',
                                 width: '100%',
                                 height: '100%',
                                 minHeight: 0,
                                 justifyContent: 'center',
                                 overflow: 'hidden',
                                 pointerEvents: readOnly ? 'none' : undefined,
                             }}
                        ></div>
                    }
                </Box>

                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        bottom: 8,
                        left: "50%",
                        zIndex: 2,
                        width: 72,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: uiConfig?.textColor || "grey.700",
                        opacity: 0.55,
                        transform: "translateX(-50%)",
                    }}
                />
            </Box>
        </Box>
    )

//endregion UI
}

export default Preview
